// src/components/Scan.jsx

import React, { useState, useEffect, useRef, Suspense } from 'react';
import {
  doc, getDoc, updateDoc, serverTimestamp, setDoc, increment,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, app } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF } from '@react-three/drei';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import * as THREE from 'three';
import toast from 'react-hot-toast';
import { useSubscription } from '../hooks/useSubscription';
import Paywall from './Paywall';
import { getScanQuota } from '../utils/referral';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

const functions          = getFunctions(app, 'us-central1');
const storage            = getStorage(app);
const renderHairCallable = httpsCallable(functions, 'renderHair', { timeout: 600000 });
const DUMMY_GLB          = '/models/fallback.glb';

// ─────────────────────────────────────────────────────────────────────────────
//  RODIN FRAME SELECTOR
//
//  Rodin Gen-2 performs best with 5–8 images covering distinct angles.
//  We extract 24 frames from a 12-second head rotation, then pick 8 that
//  are evenly spaced across the full rotation sweep:
//    i=0: 0%  → front face
//    i=1: 14% → left-front quarter
//    i=2: 28% → left side
//    i=3: 43% → left-back
//    i=4: 57% → back-right
//    i=5: 71% → right side
//    i=6: 86% → right-front
//    i=7: 100% → crown / top (end of scan)
//
//  For direct image uploads (3–5 photos), all photos are used as-is —
//  the function returns them unchanged when frames.length <= target.
// ─────────────────────────────────────────────────────────────────────────────
function selectFramesForRodin(frames, target = 8) {
  if (!frames || frames.length === 0) return frames;
  if (frames.length <= target) return frames;         // ≤ target: use all
  return Array.from({ length: target }, (_, i) =>
    frames[Math.round((frames.length - 1) * (i / (target - 1)))]
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MIME TYPE PROBE (Safari / iOS fix)
// ─────────────────────────────────────────────────────────────────────────────
function getSupportedMimeType() {
  const candidates = [
    'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9',      'video/webm;codecs=vp8',
    'video/webm', 'video/mp4;codecs=avc1', 'video/mp4', '',
  ];
  for (const type of candidates) {
    if (type === '' || (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type))) {
      return type;
    }
  }
  return '';
}

// ─────────────────────────────────────────────────────────────────────────────
//  24-FRAME EXTRACTION — extract dense temporal coverage from video,
//  then selectFramesForRodin() picks the 8 best angular views before upload.
// ─────────────────────────────────────────────────────────────────────────────
function extractFramesFromVideo(videoBlob, count = 24) {
  return new Promise((resolve) => {
    const vid    = document.createElement('video');
    const objUrl = URL.createObjectURL(videoBlob);
    vid.src = objUrl; vid.muted = true; vid.playsInline = true;

    vid.onloadedmetadata = async () => {
      const dur   = Math.max(vid.duration || 12, 1);
      const fracs = Array.from({ length: count }, (_, i) => (i + 0.5) / count);
      const files = [];

      for (let i = 0; i < fracs.length; i++) {
        const targetTime = Math.min(fracs[i] * dur, dur - 0.05);
        await new Promise((rs) => {
          vid.currentTime = targetTime;
          const onSeeked = () => { vid.removeEventListener('seeked', onSeeked); rs(); };
          vid.addEventListener('seeked', onSeeked);
          setTimeout(rs, 1200);
        });
        const canvas = document.createElement('canvas');
        canvas.width  = vid.videoWidth  || 640;
        canvas.height = vid.videoHeight || 480;
        canvas.getContext('2d').drawImage(vid, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise(rs => canvas.toBlob(rs, 'image/jpeg', 0.93));
        if (blob) {
          const frac  = fracs[i];
          const label = frac < 0.20 ? `front_${i}` : frac < 0.50 ? `left_${i}` : frac < 0.80 ? `right_${i}` : `crown_${i}`;
          files.push(new File([blob], `video_${label}.jpg`, { type: 'image/jpeg' }));
        }
      }
      URL.revokeObjectURL(objUrl);
      resolve(files.length > 0 ? files : null);
    };

    vid.onerror = () => { URL.revokeObjectURL(objUrl); resolve(null); };
    vid.load();
  });
}

// ═══════════════════════════════════════════════════════════
//  3D COMPONENTS
// ═══════════════════════════════════════════════════════════
function ProceduralHeadMesh() {
  return (
    <group>
      <mesh position={[0, 0.2, 0]}>
        <sphereGeometry args={[1.2, 32, 32]} />
        <meshStandardMaterial color="#e0d4b8" roughness={0.6} metalness={0.1} />
      </mesh>
      <mesh position={[0, -1.1, 0]}>
        <cylinderGeometry args={[0.4, 0.45, 0.9, 32]} />
        <meshStandardMaterial color="#e0d4b8" roughness={0.7} />
      </mesh>
      <mesh position={[-1.3, 0.1, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.25, 0.25, 0.3, 32]} />
        <meshStandardMaterial color="#e0d4b8" roughness={0.8} />
      </mesh>
      <mesh position={[1.3, 0.1, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <cylinderGeometry args={[0.25, 0.25, 0.3, 32]} />
        <meshStandardMaterial color="#e0d4b8" roughness={0.8} />
      </mesh>
      <ambientLight intensity={0.8} />
      <pointLight position={[10, 10, 10]} intensity={1.5} />
      <pointLight position={[-5, -5, 5]}  intensity={0.5} />
    </group>
  );
}

class SceneErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { if (this.props.onError) this.props.onError(err); }
  render() { return this.state.hasError ? <ProceduralHeadMesh /> : this.props.children; }
}

function OBJHairMesh({ hairstyleUrl }) {
  const hairData = useLoader(OBJLoader, hairstyleUrl);
  return hairData ? <primitive object={hairData} scale={1.51} /> : null;
}
function GLBHairMesh({ hairstyleUrl }) {
  const { scene } = useGLTF(hairstyleUrl);
  return scene ? <primitive object={scene} scale={1.51} /> : null;
}

function Scene({ headModelUrl, hairstyleUrl, viewMode, onModelError, useFallback }) {
  const { scene: headScene } = useGLTF(headModelUrl || DUMMY_GLB, undefined, onModelError);
  useEffect(() => {
    if (!headScene) return;
    headScene.traverse(c => { if (c.isMesh && c.material) c.material.wireframe = (viewMode === 'mesh'); });
  }, [headScene, viewMode]);

  if (useFallback || !headModelUrl) return <ProceduralHeadMesh />;
  const isHairOBJ = hairstyleUrl?.toLowerCase().endsWith('.obj');
  const showHair  = hairstyleUrl && viewMode === 'style';
  return (
    <group>
      {headScene && <primitive object={headScene} scale={1.5} />}
      {showHair && isHairOBJ  && <OBJHairMesh hairstyleUrl={hairstyleUrl} />}
      {showHair && !isHairOBJ && <GLBHairMesh hairstyleUrl={hairstyleUrl} />}
      <ambientLight intensity={0.9} />
      <pointLight position={[5, 8, 8]}   intensity={1.8} color="#ffffff" />
      <pointLight position={[-5, -3, 5]} intensity={0.6} color="#ffeedd" />
      <pointLight position={[0, 3, -5]}  intensity={0.4} color={GOLD} />
    </group>
  );
}

function CranialView({ headUrl, hairUrl, viewMode, onModelError, useFallback }) {
  return (
    <div style={meshContainer}>
      <Canvas camera={{ position: [0, 0.1, 3.0], fov: 44 }} gl={{ preserveDrawingBuffer: true, antialias: true }}>
        <color attach="background" args={['#0d1520']} />
        <Suspense fallback={null}>
          <SceneErrorBoundary onError={onModelError}>
            <Stage environment="city" intensity={0.7} contactShadow={false}>
              <Scene headModelUrl={headUrl} hairstyleUrl={hairUrl} viewMode={viewMode} onModelError={onModelError} useFallback={useFallback} />
            </Stage>
          </SceneErrorBoundary>
        </Suspense>
        <OrbitControls enableZoom={false} makeDefault />
      </Canvas>
    </div>
  );
}

function ProceduralHeadViewer() {
  return (
    <div style={meshContainer}>
      <Canvas camera={{ position: [0, 0.1, 3.5], fov: 44 }} gl={{ preserveDrawingBuffer: true, antialias: true }}>
        <color attach="background" args={['#0d1520']} />
        <Suspense fallback={null}><ProceduralHeadMesh /></Suspense>
        <OrbitControls enableZoom={false} makeDefault />
      </Canvas>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function Scan() {
  const navigate = useNavigate();
  const sub      = useSubscription();

  const [status,           setStatus]          = useState('loading');
  const [progress,         setProgress]        = useState(0);
  const [instruction,      setInstruction]     = useState('Center your face');
  const [recordedVideoUrl, setRecordedVideoUrl]= useState(null);
  const [recordedBlob,     setRecordedBlob]    = useState(null);
  const [renderUrl,        setRenderUrl]       = useState(null);
  const [hairUrl,          setHairUrl]         = useState(null);
  const [viewMode,         setViewMode]        = useState('style');
  const [error,            setError]           = useState(null);
  const [modelLoadError,   setModelLoadError]  = useState(false);
  const [isModelLoading,   setIsModelLoading]  = useState(false);
  const [glbLoadProgress,  setGlbLoadProgress] = useState(0);
  const [useFallbackMesh,  setUseFallbackMesh] = useState(false);
  const [showPaywall,      setShowPaywall]     = useState(false);
  const [paywallReason,    setPaywallReason]   = useState(null);
  const [frameCount,       setFrameCount]      = useState(0);
  const [bgStatus,         setBgStatus]        = useState('');
  const [faceLandmarker,   setFaceLandmarker]  = useState(null);
  const [meshLandmarks,    setMeshLandmarks]   = useState(null);
  const [healthVectors,    setHealthVectors]   = useState(null);
  const [frontImageUrl,    setFrontImageUrl]   = useState(null);
  const [cameraStream,     setCameraStream]    = useState(null);
  const [userIP,           setUserIP]          = useState(null);
  const [otpSent,          setOtpSent]         = useState(false);
  const [otpInput,         setOtpInput]        = useState('');
  const [uploadingImages,  setUploadingImages] = useState(false);

  const [slotImages,   setSlotImages]   = useState({ front: null, left: null, right: null, chin: null, top: null });
  const [slotPreviews, setSlotPreviews] = useState({ front: null, left: null, right: null, chin: null, top: null });

  const slotInputRefs = {
    front: useRef(null), left: useRef(null), right: useRef(null), chin: useRef(null), top: useRef(null),
  };

  const videoRef            = useRef(null);
  const mediaRecorderRef    = useRef(null);
  const recordedChunks      = useRef([]);
  const progressIntervalRef = useRef(null);

  // ── MediaPipe init ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { FaceLandmarker, FilesetResolver } = await import(
          /* webpackIgnore: true */
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/vision_bundle.mjs'
        );
        const fsr = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm'
        );
        const lm = await FaceLandmarker.createFromOptions(fsr, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
            delegate: 'CPU',
          },
          runningMode: 'IMAGE', numFaces: 1,
        });
        setFaceLandmarker(lm);
      } catch (err) { console.error('MediaPipe init failed:', err); }
    })();
  }, []);

  // ── Auth & IP ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(r => r.json()).then(d => setUserIP(d.ip)).catch(() => {});

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate('/login'); return; }
      const snap = await getDoc(doc(db, 'users', u.uid));
      if (snap.exists() && snap.data()?.hasCompletedScan && !window.location.search.includes('force=true')) {
        const d = snap.data();
        setRenderUrl(d.initialRenderUrl || d.lastScanUrl || null);
        setHairUrl(d.chosenHairstyleUrl || null);
        setFrontImageUrl(d.frontImageUrl || null);
        if (d.meshLandmarks) setMeshLandmarks(d.meshLandmarks);
        if (d.healthVectors) setHealthVectors(d.healthVectors);
        setStatus('exists');
      } else { setStatus('consent'); }
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if ((status === 'environment' || status === 'scanning') && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [status, cameraStream]);

  useEffect(() => {
    return () => { if (progressIntervalRef.current) clearInterval(progressIntervalRef.current); };
  }, []);

  // ── Quota check ────────────────────────────────────────────────────────────
  const checkScanQuota = () => {
    if (sub.loading) return false;
    if (sub.isSuperAdmin || sub.isSubscriptionActive) return true;
    const remainingFree = getScanQuota({
      lifetimeScansUsed: sub.lifetimeScansUsed,
      bonusScans:        sub.bonusScans,
    });
    const allowed = remainingFree > 0;
    if (!allowed) { setPaywallReason('free_exhausted'); setShowPaywall(true); return false; }
    return true;
  };

  const updateScanMetrics = async () => {
    try {
      await updateDoc(doc(db, 'admin', 'ai_metrics'), {
        totalScans: increment(1),
        [`daily.${new Date().getHours()}`]: increment(1),
        [`weekly.${new Date().getDay()}`]:  increment(1),
      });
    } catch {}
  };

  const incrementScanCount = async (uid) => {
    try { await updateDoc(doc(db, 'users', uid), { lifetimeScansUsed: increment(1) }); } catch {}
  };

  const transitionToDone = (url) => {
    setRenderUrl(url); setIsModelLoading(true); setGlbLoadProgress(10);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    let prog = 10;
    progressIntervalRef.current = setInterval(() => {
      prog += Math.random() * 15 + 8;
      if (prog > 92) prog = 92;
      setGlbLoadProgress(Math.min(prog, 92));
    }, 220);
    setTimeout(() => {
      clearInterval(progressIntervalRef.current);
      setStatus('done'); setIsModelLoading(false); setGlbLoadProgress(100);
    }, 1400);
  };

  const handleModelLoadError = (err) => {
    console.error('3D Model load failed:', err);
    setModelLoadError(true); setUseFallbackMesh(true); setIsModelLoading(false);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    toast.error('3D model failed to load — showing procedural mesh');
  };

  // ── Camera ─────────────────────────────────────────────────────────────────
  const startCamera = async () => {
    if (!checkScanQuota()) return;
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1080 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } }
      });
      setCameraStream(s); setStatus('environment');
    } catch { setError('Camera access denied.'); toast.error('Camera access denied.'); }
  };

  const stopCamera = () => {
    if (cameraStream) { cameraStream.getTracks().forEach(t => t.stop()); setCameraStream(null); }
  };

  const startRecording = () => {
    recordedChunks.current = []; setStatus('scanning');
    const mimeType = getSupportedMimeType();
    let mr;
    try { mr = new MediaRecorder(videoRef.current.srcObject, mimeType ? { mimeType } : {}); }
    catch { mr = new MediaRecorder(videoRef.current.srcObject); }
    mediaRecorderRef.current = mr;
    mr.ondataavailable = (e) => { if (e.data.size > 0) recordedChunks.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(recordedChunks.current, { type: mr.mimeType || 'video/webm' });
      setRecordedBlob(blob);
      setRecordedVideoUrl(URL.createObjectURL(blob));
      stopCamera(); setStatus('review');
    };
    mr.start();
    const startTime = Date.now();
    const duration  = 12000;
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct     = Math.min((elapsed / duration) * 100, 100);
      setProgress(pct);
      if      (pct < 15) setInstruction('Hold still — mapping facial anchors...');
      else if (pct < 35) setInstruction('Slowly turn LEFT ← (keep turning)');
      else if (pct < 55) setInstruction('Continue turning — show the BACK of your head');
      else if (pct < 75) setInstruction('Slowly turn RIGHT →');
      else if (pct < 90) setInstruction('Tilt head UP slightly ↑');
      else               setInstruction('Back to centre — final capture...');
      if (elapsed >= duration) { clearInterval(timer); if (mr.state !== 'inactive') mr.stop(); }
    }, 100);
  };

  // ── Direct image slots ─────────────────────────────────────────────────────
  const handleSlotSelect = (slot, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setSlotImages(p   => ({ ...p, [slot]: file }));
    setSlotPreviews(p => ({ ...p, [slot]: URL.createObjectURL(file) }));
    setError(null);
  };
  const clearSlot = (slot) => {
    setSlotImages(p   => ({ ...p, [slot]: null }));
    setSlotPreviews(p => ({ ...p, [slot]: null }));
    if (slotInputRefs[slot].current) slotInputRefs[slot].current.value = '';
  };
  const filledSlotCount      = () => Object.values(slotImages).filter(Boolean).length;
  const getOrderedSlotImages = () =>
    ['front', 'left', 'right', 'chin', 'top'].map(s => slotImages[s]).filter(Boolean);

  // ── Main pipeline ──────────────────────────────────────────────────────────
  const runPipeline = async (frontFile, allFiles, source = 'direct') => {
    const user = auth.currentUser;
    if (!user) return;
 
    setUploadingImages(true);
    setError(null);
    setUseFallbackMesh(false);
    setModelLoadError(false);
 
    try {
      setStatus('processing_mesh');
      let landmarks = null, hv = null;
 
      // ── MediaPipe landmark detection (front frame only) ───────────────────
      // This still runs — we need health vectors and landmark data for the
      // Firestore record. NOT used for client-side cropping anymore.
      if (faceLandmarker && frontFile) {
        try {
          const img    = new Image();
          const objUrl = URL.createObjectURL(frontFile);
          img.src = objUrl;
          await new Promise(r => { img.onload = r; img.onerror = r; });
          URL.revokeObjectURL(objUrl);
 
          const result = faceLandmarker.detect(img);
          if (result.faceLandmarks?.length > 0) {
            landmarks = result.faceLandmarks[0];
            const d  = (a, b) => Math.hypot(a.x-b.x, a.y-b.y, (a.z||0)-(b.z||0));
            const tw = d(landmarks[234], landmarks[454]);
            const fh = d(landmarks[10],  landmarks[152]);
            const jw = d(landmarks[172], landmarks[397]);
            const es = d(landmarks[33],  landmarks[263]);
            const lh = d(landmarks[234], landmarks[1]);
            const rh = d(landmarks[454], landmarks[1]);
            hv = {
              temporalWidth:        +tw.toFixed(4),
              faceHeight:           +fh.toFixed(4),
              jawlineWidth:         +jw.toFixed(4),
              eyeSpan:              +es.toFixed(4),
              facialSymmetry:       +(1 - Math.abs(lh-rh)/tw).toFixed(4),
              hairlineScore:        +(1 - landmarks[10].y).toFixed(4),
              follicleDensityScore: +Math.min(1, 0.5 + (1-landmarks[10].y)*0.5).toFixed(4),
              faceWidthToHeight:    +(tw/fh).toFixed(4),
            };
            setMeshLandmarks(landmarks);
            setHealthVectors(hv);
          }
        } catch (mpErr) {
          console.warn('MediaPipe failed (non-critical):', mpErr.message);
        }
     }
 
     // ── Upload RAW files directly — NO client-side preprocessing ─────────
     //
     //  FIX: Do NOT call preprocessForRodin() here.
     //  The server-side processImagesWithBgRemoval() (triggered by rawImages:true)
     //  handles bg removal and grey compositing in a single clean pass.
     //
     //  Why this is better:
     //    1. remove.bg API (server) > WASM @imgly/background-removal (client)
     //       remove.bg has dedicated ML models tuned for people; WASM timed out
     //       on every frame in testing.
     //    2. One processing pass = clean grey composite with accurate edges.
     //       Two passes = remove.bg trying to cut a grey person from grey canvas
     //       → partial face removal → white patches → desaturated output.
     //    3. No WASM download on the user's device = faster page load.
     //
     setStatus('uploading');
     toast.loading('Uploading images for server processing...', { id: 'upload' });
 
     const uploadedUrls = [];
     for (let i = 0; i < allFiles.length; i++) {
       const fileRef = ref(storage, `biometric_scans/${user.uid}/frame_${i}.jpg`);
       await uploadBytes(fileRef, allFiles[i]);
       uploadedUrls.push(await getDownloadURL(fileRef));
     }
 
     toast.dismiss('upload');
     setFrontImageUrl(uploadedUrls[0]);
     setStatus('processing_mesh');
     setFrameCount(allFiles.length);
 
     // ── Call Cloud Function ───────────────────────────────────────────────
     //
     //  rawImages: true → server runs processImagesWithBgRemoval() once.
     //  This is now the ONLY bg removal + grey composite pass.
     //
     const res = await renderHairCallable({
       rawImages:     true,          // server handles bg removal (single pass)
       images:        uploadedUrls,
       landmarks:     landmarks
         ? landmarks.map(p => ({
             x: +p.x.toFixed(5),
             y: +p.y.toFixed(5),
             z: +(p.z||0).toFixed(5),
           }))
         : null,
       healthVectors: hv,
       source,
       timestamp:     Date.now(),
     });
 
     if (res.data?.success && res.data.url) {
       await updateScanMetrics();
       await incrementScanCount(user.uid);
       toast.success('Photorealistic 3D Scan Complete!');
       transitionToDone(res.data.url);
 
     } else if (res.data?.isPlaceholder) {
       if (res.data?.reason === 'quota_exceeded') {
         setPaywallReason(res.data.paywallReason || 'free_exhausted');
         setShowPaywall(true);
         setStatus('consent');
       } else {
         await updateScanMetrics();
         toast('AI reconstruction unavailable — showing procedural mesh', { icon: '⚠️' });
         setUseFallbackMesh(true);
         setStatus('done');
       }
     } else {
       throw new Error(res.data?.error || 'No render URL returned');
     }
 
   } catch (err) {
     console.error('Pipeline error:', err);
     toast.dismiss('upload');
     const msg = err.message || 'Processing failed';
     if (msg.includes('quota')) {
       setPaywallReason('free_exhausted');
       setShowPaywall(true);
       setStatus('consent');
     } else if (msg.includes('401')) {
       toast.error('Authentication error. Please log out and back in.');
     } else {
       toast.error('Scan failed. Please try again.');
       setError(msg);
       setStatus(source === 'video' ? 'review' : 'image-select');
     }
   } finally {
     setUploadingImages(false);
   }
 };

  // ── VIDEO UPLOAD — ALIGNED WITH RODIN 5–8 IMAGE RANGE ────────────────────
  //
  //  Flow:
  //    1. Extract 24 frames from 12-second video (dense temporal coverage).
  //    2. selectFramesForRodin() picks 8 evenly-spaced angular views.
  //       → Saves 16 Firebase Storage writes + 16 remove.bg API calls.
  //    3. Upload only those 8 preprocessed frames.
  //    4. Backend receives 8 URLs → bg removal on 8 → 8 ≤ RODIN_TARGET(8) → all sent to Rodin.
  //
  const handleUpload = async () => {
    const user = auth.currentUser;
    if (!user)   return toast.error('Please log in first.');
    if (!userIP) return toast.error('Verifying identity...');

    setStatus('verifying_device');
    try {
      const ipSnap = await getDoc(doc(db, 'ip_locks', userIP));
      if (ipSnap.exists() && ipSnap.data().ownerUid !== user.uid) {
        setStatus('lock_conflict'); return;
      }

      setStatus('uploading');
      const blob    = recordedBlob || await fetch(recordedVideoUrl).then(r => r.blob());
      const fileRef = ref(storage, `scans/${user.uid}/head_scan.webm`);
      await uploadBytes(fileRef, blob);

      // Step 1: extract 24 frames for maximum temporal coverage
      toast.loading('Extracting 24 frames from video...', { id: 'frame-extract' });
      setFrameCount(0);
      const allFrames = await extractFramesFromVideo(blob, 24);
      toast.dismiss('frame-extract');

      if (!allFrames || allFrames.length === 0) {
        // Fallback: single frame from video
        const vid = document.createElement('video');
        vid.src = URL.createObjectURL(blob);
        await new Promise(r => { vid.onloadeddata = r; });
        const cv = document.createElement('canvas');
        cv.width = vid.videoWidth; cv.height = vid.videoHeight;
        cv.getContext('2d').drawImage(vid, 0, 0);
        const fb     = await new Promise(r => cv.toBlob(r, 'image/jpeg', 0.92));
        const single = new File([fb], 'front.jpg', { type: 'image/jpeg' });
        toast.warning('Frame extraction failed — using single frame');
        await runPipeline(single, [single], 'video_single_frame');
        return;
      }

      // ── ALIGNMENT FIX: pre-select 8 frames before upload ─────────────────
      // Rodin Gen-2 optimal range: 5–8 images.
      // Selecting 8 here prevents uploading 24 and wasting 16 storage writes
      // + 16 server-side remove.bg calls in renderHair.
      const rodinFrames = selectFramesForRodin(allFrames, 5);

      await setDoc(doc(db, 'ip_locks', userIP), { ownerUid: user.uid, lastActive: serverTimestamp() });
      toast.success(
        `Selected ${rodinFrames.length} best frames of ${allFrames.length} — building photorealistic 3D scan...`
      );
      setFrameCount(rodinFrames.length);

      await runPipeline(rodinFrames[0], rodinFrames, 'video_multi_frame_8');

    } catch (err) {
      console.error('handleUpload error:', err);
      setError(err.message); setStatus('review');
      toast.error('Upload failed. Please try again.');
    }
  };

  const downloadReport = () => {
    const hv  = healthVectors || {};
    const html = `<html><body style="background:#001F3F;color:white;font-family:sans-serif;padding:40px;">
<h1 style="color:#B8860B;">ESDRAS 3D CRANIAL REPORT</h1>
<p>User: ${auth.currentUser?.uid}</p><p>Generated: ${new Date().toLocaleString()}</p>
<h2 style="color:#B8860B;">Cranial Health Vectors</h2>
<table style="border-collapse:collapse;width:100%;">
${Object.entries(hv).map(([k,v]) =>
  `<tr style="border-bottom:1px solid rgba(184,134,11,0.3)">
    <td style="padding:8px;color:#B8860B;">${k.replace(/([A-Z])/g,' $1').toUpperCase()}</td>
    <td style="padding:8px;font-weight:bold;">${v}</td>
  </tr>`).join('')}
</table>
<p style="opacity:0.5;margin-top:30px;font-size:0.7rem;">Esdras Biometric Engine</p>
</body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const a = document.createElement('a'); a.href = url; a.download = 'Esdras_Cranial_Report.html'; a.click();
    toast.success('Cranial Report Generated');
  };

  // ─── RENDER ────────────────────────────────────────────────────────────────
  if (status === 'loading' || sub.loading) {
    return (
      <div style={fullCenter}>
        <div style={spinner} />
        <p style={{ color: GOLD, letterSpacing: '3px', fontSize: '0.7rem', marginTop: '20px' }}>INITIALIZING...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: NAVY, color: 'white', textAlign: 'center', padding: '20px', fontFamily: 'Montserrat, sans-serif' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {showPaywall && <Paywall reason={paywallReason} onClose={() => setShowPaywall(false)} redirectAfter="/scan?force=true" />}

      <div style={{ marginBottom: '20px' }}>
        <img src="/icons/esdras-logo.png" style={{ width: '80px' }} alt="Esdras" onError={e => { e.target.style.display = 'none'; }} />
      </div>

      {/* ── CONSENT ─────────────────────────────────────────────────────────── */}
      {status === 'consent' && (
        <div style={containerStyle}>
          <h2 style={{ color: GOLD }}>Photorealistic Biometric Scan</h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginBottom: '20px' }}>
            We extract 24 frames and select the 8 best angular views for Rodin reconstruction.
            Hold phone at arm's length (40–50 cm). Rotate head SLOWLY for 12 seconds — make sure
            the camera sees the BACK of your head.
          </p>
          <div style={{ margin: '20px 0' }}>
            <button onClick={startCamera} style={btnStyle}>📹 RECORD VIDEO SCAN (Recommended)</button>
            <p style={{ color: '#555', fontSize: '0.7rem', margin: '8px 0' }}>
              Rotate head slowly for 12 seconds — 8 angular views selected automatically
            </p>
            <button
              onClick={() => {
                if (!checkScanQuota()) return;
                setSlotImages(  { front: null, left: null, right: null, chin: null, top: null });
                setSlotPreviews({ front: null, left: null, right: null, chin: null, top: null });
                setStatus('image-select');
              }}
              style={{ ...btnStyle, background: 'transparent', border: `2px solid ${GOLD}`, color: GOLD, marginTop: '12px' }}>
              📸 UPLOAD 3–5 PHOTOS INSTEAD
            </button>
          </div>
          <div style={consentBox}>
            <p style={{ color: GOLD, fontWeight: 'bold' }}>✓ Hyper3D Rodin Gen-2 · 8 Angular Views · 4K PBR · Face-Centered Crop</p>
            <p style={{ fontSize: '0.8rem', opacity: 0.7 }}>Exact likeness. Background removed. Your face, your identity.</p>
            {!sub.isSubscriptionActive && !sub.isSuperAdmin && (
              <p style={{ fontSize: '0.7rem', color: '#ff9900', marginTop: '10px' }}>
                ⚡ 1 free scan included — subscribe for monthly access
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── IMAGE SELECT ─────────────────────────────────────────────────────── */}
      {status === 'image-select' && (
        <div style={containerStyle}>
          <h2 style={{ color: GOLD }}>Biometric Photo Upload</h2>
          <p style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '12px', lineHeight: '1.6' }}>
            Upload one photo per slot. <strong style={{ color: GOLD }}>Front face is required.</strong><br />
            <span style={{ color: '#ff9900', fontSize: '0.72rem' }}>
              💡 Tip: Good lighting + plain background = best results.
            </span>
          </p>

          {['front', 'left', 'right', 'chin', 'top'].map(slot => (
            <input
              key={slot}
              ref={slotInputRefs[slot]}
              type="file"
              accept="image/*"
              onChange={e => handleSlotSelect(slot, e)}
              style={{ display: 'none' }}
            />
          ))}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '25px' }}>
            {[
              { slot: 'front', label: 'FRONT FACE', icon: '⚡', required: true,  hint: null },
              { slot: 'left',  label: 'LEFT SIDE',  icon: '◀',  required: false, hint: null },
              { slot: 'right', label: 'RIGHT SIDE', icon: '▶',  required: false, hint: null },
              { slot: 'chin',  label: 'CHIN VIEW',  icon: '↓',  required: false, hint: null },
              {
                slot: 'top', label: 'CROWN VIEW', icon: '↑', required: false,
                hint: 'Hold phone above your head pointing DOWN — shows the crown',
                style: { gridColumn: '1 / -1' },
              },
            ].map(({ slot, label, icon, required, hint, style: extraStyle = {} }) => {
              const hasImage = !!slotPreviews[slot];
              const isFront  = slot === 'front';
              return (
                <div
                  key={slot}
                  onClick={() => slotInputRefs[slot].current.click()}
                  style={{
                    position: 'relative', aspectRatio: slot === 'top' ? '2/1' : '1/1',
                    borderRadius: '18px',
                    border: hasImage
                      ? `3px solid ${isFront ? '#00ff88' : GOLD}`
                      : `2px dashed ${isFront ? 'rgba(0,255,136,0.5)' : 'rgba(184,134,11,0.4)'}`,
                    background: hasImage ? 'transparent' : 'rgba(255,255,255,0.03)',
                    cursor: 'pointer', overflow: 'hidden',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    ...extraStyle,
                  }}
                >
                  {hasImage ? (
                    <>
                      <img src={slotPreviews[slot]} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button onClick={e => { e.stopPropagation(); clearSlot(slot); }} style={clearBtn}>✕</button>
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: isFront ? 'rgba(0,255,136,0.85)' : 'rgba(0,0,0,0.72)', color: isFront ? '#000' : '#fff', fontSize: '0.5rem', fontWeight: '900', textAlign: 'center', padding: '4px', letterSpacing: '1.5px' }}>
                        {label} ✓
                      </div>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '1.6rem', marginBottom: '4px', opacity: 0.6 }}>{icon}</span>
                      <span style={{ fontSize: '0.55rem', fontWeight: '900', letterSpacing: '1.5px', color: isFront ? '#00ff88' : GOLD }}>
                        {label}
                      </span>
                      {required && <span style={{ fontSize: '0.5rem', color: '#ff6b6b', marginTop: '3px' }}>REQUIRED</span>}
                      {hint ? (
                        <span style={{ fontSize: '0.48rem', opacity: 0.55, marginTop: '5px', color: GOLD, textAlign: 'center', padding: '0 12px', lineHeight: 1.5 }}>{hint}</span>
                      ) : (
                        <span style={{ fontSize: '0.5rem', opacity: 0.4, marginTop: '4px' }}>TAP TO UPLOAD</span>
                      )}
                      {slot === 'top' && <span style={{ fontSize: '0.45rem', opacity: 0.35, marginTop: '3px' }}>TAP TO UPLOAD</span>}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px', alignItems: 'center' }}>
            {['front', 'left', 'right', 'chin', 'top'].map(s => (
              <div key={s} style={{ width: '8px', height: '8px', borderRadius: '50%', background: slotImages[s] ? (s === 'front' ? '#00ff88' : GOLD) : 'rgba(255,255,255,0.15)' }} />
            ))}
            <span style={{ fontSize: '0.6rem', opacity: 0.6, marginLeft: '4px' }}>{filledSlotCount()} of 5 uploaded</span>
          </div>

          {slotImages.front && filledSlotCount() >= 3 ? (
            <button onClick={handleDirectImageUpload} disabled={uploadingImages}
              style={{ ...btnStyle, background: '#00ff88', color: '#000', fontSize: '1rem', fontWeight: '900' }}>
              {uploadingImages ? 'RECONSTRUCTING...' : 'GENERATE PHOTOREALISTIC 3D HEAD'}
            </button>
          ) : (
            <div style={infoBox}>
              {!slotImages.front ? '⚡ Upload front face photo to continue' : `Upload ${3 - filledSlotCount()} more photo(s) to generate`}
            </div>
          )}
          <button onClick={() => setStatus('consent')} style={outlineBtn}>← BACK TO VIDEO MODE</button>
        </div>
      )}

      {/* ── VIDEO REVIEW ─────────────────────────────────────────────────────── */}
      {status === 'review' && (
        <div style={containerStyle}>
          <h2 style={{ color: GOLD }}>Verify Video Quality</h2>
          <video src={recordedVideoUrl} autoPlay loop muted playsInline style={{ ...videoStyles, borderRadius: '24px' }} />
          <p style={{ color: '#888', fontSize: '0.72rem', margin: '8px 0 0' }}>
            ✓ 8 angular views selected · background removed · face-centered crop
          </p>
          <button onClick={handleUpload} style={btnStyle}>GENERATE PHOTOREALISTIC 3D HEAD</button>
          <button onClick={() => { setRecordedVideoUrl(null); setRecordedBlob(null); startCamera(); }} style={outlineBtn}>RE-SCAN</button>
        </div>
      )}

      {/* ── CAMERA ───────────────────────────────────────────────────────────── */}
      {(status === 'environment' || status === 'scanning') && (
        <div style={containerStyle}>
          <div style={statusBadge}>{status === 'scanning' ? 'CAPTURING FRAMES...' : 'SENSOR READY'}</div>
          <h3 style={{ height: '60px', color: GOLD }}>{instruction}</h3>
          <div style={videoWrapper}>
            <video ref={videoRef} autoPlay playsInline muted style={videoStyles} />
            {status === 'scanning' && (
              <div style={progressRing}>
                <svg viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(184,134,11,0.2)" strokeWidth="4" />
                  <circle cx="50" cy="50" r="45" fill="none" stroke={GOLD} strokeWidth="4"
                    strokeDasharray={`${progress * 2.83} 283`} strokeLinecap="round" />
                </svg>
              </div>
            )}
          </div>
          {status === 'environment' && <button onClick={startRecording} style={btnStyle}>START BIOMETRIC SCAN</button>}
        </div>
      )}

      {/* ── PROCESSING ───────────────────────────────────────────────────────── */}
      {(status === 'uploading' || status === 'processing_mesh' || status === 'verifying_device') && (
        <div style={containerStyle}>
          <div style={loaderContainer}><div style={spinner} /><div style={scanLineAnim} /></div>
          <h3 style={{ color: GOLD, letterSpacing: '3px', fontSize: '0.85rem' }}>
            {status === 'uploading'        ? (bgStatus || 'UPLOADING BIOMETRIC DATA...')    :
             status === 'processing_mesh'  ? 'PHOTOREALISTIC RECONSTRUCTION IN PROGRESS...' :
             status === 'verifying_device' ? 'VERIFYING DEVICE...'                          : ''}
          </h3>
          {status === 'processing_mesh' && (
            <div>
              <p style={{ opacity: 0.5, fontSize: '0.7rem' }}>
                Hyper3D Rodin Gen-2 is building your photorealistic 3D likeness
                {frameCount > 0 ? ` from ${frameCount} angular ${frameCount === 1 ? 'view' : 'views'}` : ''}.
              </p>
              <p style={{ opacity: 0.4, fontSize: '0.65rem', marginTop: '6px' }}>This takes 3–8 minutes.</p>
            </div>
          )}
        </div>
      )}

      {/* ── GLB LOADING ──────────────────────────────────────────────────────── */}
      {isModelLoading && (
        <div style={containerStyle}>
          <div style={loaderContainer}><div style={spinner} /></div>
          <h3 style={{ color: GOLD, letterSpacing: '3px', marginBottom: '12px' }}>LOADING YOUR PHOTOREALISTIC HEAD...</h3>
          <div style={{ width: '100%', height: '8px', background: 'rgba(184,134,11,0.15)', borderRadius: '9999px', overflow: 'hidden', marginBottom: '12px' }}>
            <div style={{ width: `${glbLoadProgress}%`, height: '100%', background: 'linear-gradient(90deg, #B8860B, #ffdd88)', transition: 'width 0.4s ease', borderRadius: '9999px' }} />
          </div>
          <p style={{ fontSize: '0.8rem', color: GOLD, fontWeight: '600' }}>{Math.floor(glbLoadProgress)}%</p>
        </div>
      )}

      {/* ── DONE / EXISTS ────────────────────────────────────────────────────── */}
      {(status === 'done' || status === 'exists') && !isModelLoading && (
        <div style={containerStyle}>
          <h2 style={{ color: GOLD }}>{status === 'done' ? 'Photorealistic Scan Complete' : '3D Identity Secured'}</h2>
          <div style={toggleWrapper}>
            <button onClick={() => setViewMode('style')} style={{ ...toggleBtn, background: viewMode === 'style' ? GOLD : 'transparent', color: viewMode === 'style' ? '#000' : GOLD }}>YOUR FACE</button>
            <button onClick={() => setViewMode('mesh')}  style={{ ...toggleBtn, background: viewMode === 'mesh'  ? GOLD : 'transparent', color: viewMode === 'mesh'  ? '#000' : GOLD }}>3D MESH</button>
          </div>
          {renderUrl && !useFallbackMesh && !modelLoadError
            ? <CranialView headUrl={renderUrl} hairUrl={hairUrl} viewMode={viewMode} onModelError={handleModelLoadError} useFallback={false} />
            : <ProceduralHeadViewer />}
          {healthVectors && (
            <div style={healthPanel}>
              <p style={{ color: GOLD, fontWeight: '900', fontSize: '0.7rem', letterSpacing: '2px', marginBottom: '12px' }}>⚡ CRANIAL HEALTH VECTORS</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {Object.entries(healthVectors).map(([k, v]) => (
                  <div key={k} style={{ background: 'rgba(0,0,0,0.4)', padding: '8px 10px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.48rem', color: GOLD, opacity: 0.8, letterSpacing: '1px' }}>{k.replace(/([A-Z])/g, ' $1').toUpperCase()}</div>
                    <div style={{ fontSize: '0.82rem', fontWeight: '800', marginTop: '2px' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => navigate('/dashboard')} style={btnStyle}>PROCEED TO DASHBOARD</button>
          <button onClick={downloadReport} style={outlineBtn}>DOWNLOAD CRANIAL REPORT</button>
          <button onClick={() => setStatus('consent')} style={{ ...outlineBtn, border: 'none', opacity: 0.5 }}>RE-CALIBRATE SCAN</button>
        </div>
      )}

      {error && (
        <div style={errorCard}>
          <p>⚠️ {error}</p>
          <button onClick={() => setError(null)} style={{ color: '#fff', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', marginTop: '10px' }}>RETRY</button>
        </div>
      )}

      {status === 'lock_conflict' && (
        <div style={containerStyle}>
          <h2 style={{ color: '#ff4d4d' }}>Security Notice</h2>
          <p>Network linked to another profile.</p>
          {!otpSent
            ? <button onClick={() => { setOtpSent(true); toast.success('Code sent'); }} style={btnStyle}>SEND VERIFICATION CODE</button>
            : <>
                <input style={inputStyle} placeholder="000000" value={otpInput} onChange={e => setOtpInput(e.target.value)} />
                <button onClick={() => { toast.success('Unlocked'); setStatus('consent'); }} style={btnStyle}>VERIFY & UNLOCK</button>
              </>
          }
        </div>
      )}
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const containerStyle = { maxWidth: '450px', margin: 'auto' };
const videoWrapper   = { position: 'relative', width: '100%', borderRadius: '30px', overflow: 'hidden', border: `1px solid ${GOLD}`, background: '#000' };
const videoStyles    = { width: '100%', display: 'block', aspectRatio: '1/1', objectFit: 'cover' };
const statusBadge    = { background: GOLD, color: '#000', padding: '6px 16px', borderRadius: '50px', fontSize: '0.65rem', fontWeight: '900', marginBottom: '15px' };
const btnStyle       = { width: '100%', padding: '18px', background: GOLD, color: 'black', borderRadius: '50px', border: 'none', fontWeight: '900', marginTop: '20px', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' };
const outlineBtn     = { width: '100%', padding: '16px', background: 'none', color: 'white', border: `1px solid rgba(184,134,11,0.4)`, borderRadius: '50px', marginTop: '12px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' };
const inputStyle     = { width: '100%', padding: '18px', borderRadius: '15px', border: '1px solid #333', background: '#000', color: GOLD, textAlign: 'center', fontSize: '1.5rem', marginTop: '20px', fontFamily: 'Montserrat, sans-serif' };
const progressRing   = { position: 'absolute', top: '20px', right: '20px', width: '60px', height: '60px' };
const meshContainer  = { height: '420px', width: '100%', borderRadius: '30px', margin: '20px 0', overflow: 'hidden', border: `1px solid ${GOLD}`, background: '#0d1520' };
const loaderContainer= { position: 'relative', height: '140px', marginBottom: '20px' };
const spinner        = { width: '60px', height: '60px', border: `3px solid rgba(184,134,11,0.1)`, borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' };
const scanLineAnim   = { position: 'absolute', top: '35%', left: '50%', transform: 'translate(-50%,-50%)', width: '80px', height: '2px', background: GOLD, boxShadow: `0 0 15px ${GOLD}` };
const toggleWrapper  = { display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '15px', background: 'rgba(0,0,0,0.3)', padding: '5px', borderRadius: '50px', border: `1px solid ${GOLD}44` };
const toggleBtn      = { padding: '8px 15px', borderRadius: '50px', border: 'none', fontSize: '0.7rem', fontWeight: '800', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' };
const consentBox     = { textAlign: 'left', background: 'rgba(255,255,255,0.05)', padding: '20px', borderRadius: '20px', borderLeft: `4px solid ${GOLD}` };
const infoBox        = { padding: '14px', borderRadius: '15px', background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.1)', fontSize: '0.75rem', opacity: 0.6, textAlign: 'center' };
const clearBtn       = { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', width: '22px', height: '22px', color: '#fff', fontSize: '0.6rem', cursor: 'pointer', fontWeight: '900' };
const healthPanel    = { background: 'rgba(184,134,11,0.07)', border: `1px solid rgba(184,134,11,0.3)`, borderRadius: '20px', padding: '20px', marginTop: '15px', textAlign: 'left' };
const errorCard      = { background: 'rgba(255,77,77,0.1)', color: '#ff4d4d', padding: '20px', borderRadius: '20px', border: '1px solid #ff4d4d', marginTop: '15px' };
const fullCenter     = { minHeight: '100vh', background: NAVY, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' };
